import { httpsCallable } from 'firebase/functions';
import { Dice5 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { ContentDeliveryType } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  checkDeliveryCodeAvailability,
  type ContentDeliveryRecord,
} from '@/hooks/useLineData';
import { functions } from '@/lib/firebase';
import { toDatetimeLocalValue } from '@/lib/firestoreTime';

const upsertFn = httpsCallable<
  {
    id?: string;
    deliveryCode: string;
    contentLink: string;
    contentType: ContentDeliveryType;
    startAt: string;
    endAt: string;
    password?: string | null;
  },
  { ok: boolean; id: string; status: string }
>(functions, 'upsertContentDelivery');

const generateFn = httpsCallable<Record<string, never>, { code: string }>(
  functions,
  'generateUniqueDeliveryCode',
);

interface FormValues {
  deliveryCode: string;
  contentLink: string;
  contentType: ContentDeliveryType;
  startAt: string;
  endAt: string;
  password: string;
}

const defaults: FormValues = {
  deliveryCode: '',
  contentLink: '',
  contentType: 'archive_playback',
  startAt: '',
  endAt: '',
  password: '',
};

interface DeliveryFormDialogProps {
  open: boolean;
  existing: ContentDeliveryRecord | null;
  onOpenChange: (open: boolean) => void;
}

export function DeliveryFormDialog({ open, existing, onOpenChange }: DeliveryFormDialogProps) {
  const form = useForm<FormValues>({ defaultValues: defaults });
  const [codeStatus, setCodeStatus] = useState<
    | { type: 'idle' }
    | { type: 'checking' }
    | { type: 'ok' }
    | { type: 'conflict'; conflictStatus: string }
    | { type: 'invalid' }
  >({ type: 'idle' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (existing) {
      form.reset({
        deliveryCode: existing.deliveryCode ?? '',
        contentLink: existing.contentLink ?? '',
        contentType: existing.contentType ?? 'archive_playback',
        startAt: toDatetimeLocalValue(existing.startAt),
        endAt: toDatetimeLocalValue(existing.endAt),
        password: existing.password ?? '',
      });
    } else if (open) {
      form.reset(defaults);
    }
    setCodeStatus({ type: 'idle' });
  }, [existing, open, form]);

  const checkCode = async (raw: string) => {
    const code = raw.trim().toUpperCase();
    if (!code) return setCodeStatus({ type: 'idle' });
    if (!/^[A-Z0-9]{6,20}$/.test(code)) return setCodeStatus({ type: 'invalid' });
    setCodeStatus({ type: 'checking' });
    try {
      const res = await checkDeliveryCodeAvailability(code, existing?.id);
      if (res.available) setCodeStatus({ type: 'ok' });
      else setCodeStatus({ type: 'conflict', conflictStatus: res.conflictStatus ?? 'active' });
    } catch {
      setCodeStatus({ type: 'idle' });
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await generateFn({});
      form.setValue('deliveryCode', res.data.code);
      await checkCode(res.data.code);
    } catch (err) {
      toast.error(`生成に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await upsertFn({
        id: existing?.id,
        deliveryCode: values.deliveryCode,
        contentLink: values.contentLink,
        contentType: values.contentType,
        startAt: new Date(values.startAt).toISOString(),
        endAt: new Date(values.endAt).toISOString(),
        password: values.password.trim() || null,
      });
      toast.success(existing ? '配信を更新しました' : '配信を作成しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? '配信を編集' : '配信を新規作成'}</DialogTitle>
          <DialogDescription>
            配信コードは英数字 6〜20 文字、大文字で保存されます
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>配信コード</Label>
            <div className="flex gap-2">
              <Input
                {...form.register('deliveryCode', { required: true })}
                onBlur={(e) => void checkCode(e.target.value)}
                placeholder="ABC123"
                autoCapitalize="characters"
                className="uppercase"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleGenerate}
                title="ランダム生成"
              >
                <Dice5 />
              </Button>
            </div>
            {codeStatus.type === 'checking' && (
              <p className="text-xs text-[var(--text-muted)] mt-1">確認中...</p>
            )}
            {codeStatus.type === 'ok' && (
              <p className="text-xs text-[var(--accent-green)] mt-1">✓ 利用可能</p>
            )}
            {codeStatus.type === 'invalid' && (
              <p className="text-xs text-[var(--accent-red)] mt-1">
                英数字 6〜20 文字で入力してください
              </p>
            )}
            {codeStatus.type === 'conflict' && (
              <p className="text-xs text-[var(--accent-red)] mt-1">
                このコードは現在 {codeStatus.conflictStatus} のレコードで使用中です
              </p>
            )}
          </div>

          <div>
            <Label>種別</Label>
            <Select
              value={form.watch('contentType')}
              onValueChange={(v) => form.setValue('contentType', v as ContentDeliveryType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="archive_playback">見逃し配信</SelectItem>
                <SelectItem value="limited_contents">限定コンテンツ</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cl">コンテンツ URL</Label>
            <Input
              id="cl"
              type="url"
              placeholder="https://..."
              {...form.register('contentLink', { required: true })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sa">開始日時</Label>
              <Input
                id="sa"
                type="datetime-local"
                {...form.register('startAt', { required: true })}
              />
            </div>
            <div>
              <Label htmlFor="ea">終了日時</Label>
              <Input
                id="ea"
                type="datetime-local"
                {...form.register('endAt', { required: true })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="pw">パスワード（任意）</Label>
            <Input
              id="pw"
              type="text"
              placeholder="未設定なら空欄のまま"
              maxLength={64}
              {...form.register('password')}
            />
            <p className="text-[11px] text-[var(--text-muted)] mt-1">
              リンク先（YouTube 等）に設定している視聴パスワード。設定すると、配信コード入力後のボット返信にリンクと合わせて記載されます。
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                submitting ||
                codeStatus.type === 'conflict' ||
                codeStatus.type === 'invalid' ||
                codeStatus.type === 'checking'
              }
            >
              {submitting ? '保存中...' : existing ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
